import {
  ContractRequest,
  useCompany,
  useDealer,
  useContractType,
  useTemplatesByCompany,
  useTemplatesByContractType,
  useContract,
  useUpdateContract,
  useSendForSigning,
  useGetImage,
} from '@api'
import {
  addTopRightPageNumbers,
  formatCurrency,
  FormFieldItem,
  generatePdf,
  getTotalContractCost,
  getUserData,
  isSuperAdmin,
  useViewNavigate,
} from '@common'
import {
  ContractStatus,
  FormFieldType,
  ImageType,
  NavigationPathKey,
  NotificationMethod,
  SigningMethod,
  TranslationKey,
} from '@enums'
import {
  getCompanyCurrencyAtom,
  getUserSelectionAtom,
  getUserTimeFormatAtom,
  pageLinksAtom,
  successMessageAtom,
} from '@state'
import {initialSigningFormData, SigningFormData} from '@state'

import {SetStateAction, useEffect, useMemo, useState} from 'react'

import {formatDate, isAfter, parse} from 'date-fns'
import {useAtomValue, useSetAtom} from 'jotai'
import jsPDF from 'jspdf'
import {useTranslation} from 'react-i18next'
import {useParams} from 'react-router-dom'

import {UIKit} from '@components'

import {generateTemplateHtml} from '../contract-form/contract-form-steps/contract-pdf-overview/contract-overview-custom-html'
import {generateHtml} from '../contract-form/contract-form-steps/contract-pdf-overview/contract-overview-html'

export function useContractViewModel() {
  const {t} = useTranslation()
  const {id} = useParams()
  const navigate = useViewNavigate()
  const [pdfUrl, setPdfUrl] = useState<string>('')
  const [pdfDoc, setPdfDoc] = useState<jsPDF | undefined>(undefined)

  const [pdfTransferUrl, setPdfTransferUrl] = useState<string>('')
  const [isSignatureOptionsModalVisible, setIsSignatureOptionsModalVisible] = useState<boolean>(false)
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false)
  const [isPdfTransferModalOpen, setIsPdfTransferModalOpen] = useState(false)
  const [signingFormData, setSigningFormData] = useState<SigningFormData>(initialSigningFormData)
  const setSuccessMessage = useSetAtom(successMessageAtom)
  const setTitleLinks = useSetAtom(pageLinksAtom)

  const currentUser = getUserData()
  const userSelections = useAtomValue(getUserSelectionAtom)
  const userSelection = userSelections[currentUser.id]

  const getUserTimeFormat = useAtomValue(getUserTimeFormatAtom)
  const userTimeFormat = getUserTimeFormat(currentUser.id)

  const getCompanyCurrency = useAtomValue(getCompanyCurrencyAtom)
  const currency = userSelection ? getCompanyCurrency(userSelection?.companyId) : 'EUR'

  const {data: contractData} = useContract(id)
  const contractStatus = useMemo(() => contractData?.status, [contractData])

  const {data: contractTemplatesData} = useTemplatesByCompany(contractData?.companyId)
  const {data: contractTypeTemplatesData} = useTemplatesByContractType(contractData?.contractTypeId)
  const {data: imageUrl} = useGetImage(ImageType.COMPANY, contractData?.companyId)

  const {mutate: updateContractMutation} = useUpdateContract()
  const {mutate: sendForSigningMutation} = useSendForSigning()

  const transferTemplate = useMemo(() => {
    if (!contractTemplatesData) return ''
    else return contractTemplatesData.find(item => item.contractType === 'TA')?.htmlContent
  }, [contractTemplatesData])

  const {data: dealerData} = useDealer(contractData?.dealerId)
  const {data: contractTypeData} = useContractType(contractData?.contractTypeId)
  const {data: companyData} = useCompany(contractData?.companyId)

  /**
   * Contract form field configuration
   */
  const contractOverviewFormFields: FormFieldItem[] = [
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Contract code']),
      text: contractData?.contractCode || '-',
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Created at']),
      text: contractData?.createdAt ? formatDate(contractData?.createdAt || '', userTimeFormat) : '-',
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(
        TranslationKey[contractData?.status !== ContractStatus.SENT_FOR_SIGNING ? 'Valid until' : 'Sent for signing'],
      ),
      text:
        contractData?.status !== ContractStatus.SENT_FOR_SIGNING
          ? contractData?.expiry
            ? formatDate(contractData?.expiry, userTimeFormat)
            : '-'
          : contractData?.updatedAt
            ? formatDate(contractData?.updatedAt, userTimeFormat)
            : formatDate(contractData?.createdAt, userTimeFormat),
    },
  ]

  const contractDetailFormFields: FormFieldItem[] = [
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Customer']),
      text: `${contractData?.customer.firstName} ${contractData?.customer.lastName}`,
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Email']),
      text: contractData?.customer.email,
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey.Dealer),
      text: dealerData?.name,
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Dealer number']),
      text: dealerData?.dealerNo,
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Official signatory']),
      text: contractData?.officialSignatory,
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Contract length']),
      text: `${contractData?.length} ${t(TranslationKey['Month(s)'])}`,
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Monthly fee']),
      text: `${formatCurrency(getTotalContractCost(contractData?.products || [], contractData?.extraCosts || []) / (contractData?.length || 1), currency)}`,
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey.Total),
      text: `${formatCurrency(getTotalContractCost(contractData?.products || [], contractData?.extraCosts || []), currency)}`,
    },
  ]

  function addSigningStamp(pdf: jsPDF, text: string, offsetMm = 10) {
    const pageCount = pdf.getNumberOfPages()
    const pageWidth = pdf.internal.pageSize.getWidth()

    pdf.setFontSize(8)
    pdf.setTextColor(120)

    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i)

      pdf.text(text, pageWidth / 2, offsetMm, {align: 'center'})
    }
  }

  useEffect(
    () =>
      setTitleLinks([
        {title: t(TranslationKey['Contract search']), link: NavigationPathKey.CONTRACT_SEARCH},
        {title: t(TranslationKey['Contract overview'])},
      ]),
    [],
  )

  const [loadingPdf, setLoadingPdf] = useState(false)

  const generateAndSetPdf = async (
    htmlData: string,
    setUrl: React.Dispatch<SetStateAction<string>>,
    ipField?: string,
    ipFieldTimestamp?: string,
  ) => {
    setLoadingPdf(true)

    const doc = await generatePdf(htmlData)
    const pdfWithNumbers = addTopRightPageNumbers(
      doc,
      (i, total) => `${t(TranslationKey.Page)} ${i} ${t(TranslationKey.of)} ${total}`,
    )

    if (ipField && ipFieldTimestamp) {
      addSigningStamp(
        pdfWithNumbers,
        `${t(TranslationKey.Signed)}: [IP: ${ipField}] [${t(TranslationKey['Timestamp'])}: ${ipFieldTimestamp}]`,
      )
    }

    const pdfBlob = pdfWithNumbers.output('blob')
    const pdfObjectUrl = URL.createObjectURL(pdfBlob)
    setUrl(pdfObjectUrl)

    setLoadingPdf(false)

    return pdfWithNumbers
  }

  useEffect(() => {
    if (!contractData || !companyData || !dealerData || !contractTypeData) return

    const generatePdfDocs = async () => {
      const dataForHtml = {
        company: companyData,
        contractCode: contractData.contractCode || '',
        customer: contractData.customer,
        contractType: contractTypeData,
        quote: contractData.status === ContractStatus.QUOTE,
        dealer: dealerData,
        disclaimer: contractTypeData?.disclaimer,
        extraCosts: contractData.extraCosts,
        length: contractData.length,
        products: contractData.products,
        signatory: contractData.officialSignatory,
        expiry: contractData.expiry,
        createdAt: formatDate(contractData.createdAt || '', userTimeFormat),
      }

      const templateHtml = contractTypeTemplatesData?.[0]?.htmlContent
      const htmlData = templateHtml
        ? generateTemplateHtml(dataForHtml, templateHtml, imageUrl || '', currency)
        : generateHtml(t, dataForHtml, imageUrl || '', currency, userTimeFormat)

      const doc = await generateAndSetPdf(htmlData, setPdfUrl)
      setPdfDoc(doc)
      const htmlTransferData = transferTemplate
        ? generateTemplateHtml(dataForHtml, transferTemplate, imageUrl || '', currency)
        : generateHtml(t, dataForHtml, imageUrl || '', currency, userTimeFormat)

      generateAndSetPdf(htmlTransferData, setPdfTransferUrl, contractData.ipField, contractData.ipFieldTimestamp)
    }

    generatePdfDocs()
  }, [
    contractData,
    companyData,
    dealerData,
    contractTypeData,
    contractTypeTemplatesData,
    transferTemplate,
    currency,
    t,
    userTimeFormat,
  ])

  /**
   * A function checking if a date if after today
   * @param dateString Date
   * @returns If date is after today
   */
  const checkDateAfterToday = (dateString: string) => {
    const inputDate = parse(dateString, userTimeFormat, new Date())

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    return isAfter(inputDate, today)
  }

  /**
   * A function handling Cancel button click
   */
  const onCancelBtnClick = () => {
    if (!id) throw new Error('no contract ID')
    updateContractMutation(
      {
        body: {...contractData, status: ContractStatus.CANCELED} as ContractRequest,
        id,
      },
      {onSuccess: () => setSuccessMessage(t(TranslationKey['Cancelled']))},
    )
  }

  /**
   * A function handling singing form fields change
   * @param event Change event
   * @param checked Checked state for checkbox form field
   */
  const onChangeSigningFormField = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    checked?: boolean,
  ) => {
    const fieldName = event.target.name
    const value = checked ?? event.target.value
    setSigningFormData(prev => ({...prev, [fieldName]: value}))
  }

  /**
   * Notification method dropdown options
   */
  const notificationMethodOptions: UIKit.DropdownItem[] = [
    {
      label: t(TranslationKey.Email),
      value: NotificationMethod.EMAIL,
    },
    {
      label: t(TranslationKey.SMS),
      value: NotificationMethod.SMS,
    },
  ]

  /**
   * Signing method dropdown options
   */
  const signingMethodOptions: UIKit.DropdownItem[] = [
    {
      value: SigningMethod.EMAIL,
      label: t(TranslationKey.Email),
    },
    {
      value: SigningMethod.SMS,
      label: t(TranslationKey.SMS),
    },
    {
      value: SigningMethod.BANKID_NO,
      label: `${t(TranslationKey['Bank ID'])} NO`,
    },
    {
      value: SigningMethod.BANKID_SE,
      label: `${t(TranslationKey['Bank ID'])} SE`,
    },
  ]

  /**
   * Siginig form fields configuration
   */
  const signingFormFields: FormFieldItem[] = [
    {
      fieldType: FormFieldType.INPUT,
      label: t(TranslationKey.Message),
      name: 'greeting',
      onChange: onChangeSigningFormField,
      value: signingFormData.greeting,
    },
    {
      fieldType: FormFieldType.DROPDOWN,
      label: t(TranslationKey['Notification method']),
      name: 'notificationMethod',
      onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onChangeSigningFormField(event),
      value: signingFormData.notificationMethod,
      items: notificationMethodOptions,
    },
    {
      fieldType: FormFieldType.DROPDOWN,
      label: t(TranslationKey['Signing method']),
      name: 'signingMethod',
      onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onChangeSigningFormField(event),
      value: signingFormData.signingMethod,
      items: signingMethodOptions,
    },
    {
      fieldType: FormFieldType.CHECKBOX_GROUP,
      name: 'sms',
      itemsInColumn: 1,
      checkboxes: [
        {
          checked: signingFormData.sms,
          label: t(TranslationKey['Send SMS notification']),
          value: signingFormData.sms,
          name: 'sms',
          onChange: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) =>
            onChangeSigningFormField(event, checked),
        },
      ],
    },
  ]

  /**
   * A function handling Send for signing button clicks
   */
  const onSendForSigningBtnClick = () => {
    setIsSignatureOptionsModalVisible(false)

    if (!pdfDoc) throw new Error('no pdf doc')
    if (!contractData) throw new Error('no contract data')
    const {customer} = contractData || {}
    const {email, firstName, lastName, phone} = customer
    sendForSigningMutation(
      {
        file: pdfDoc?.output('arraybuffer'),
        documentName: `${customer.firstName} ${customer.lastName} ${t(TranslationKey.Contract)}`,
        contractId: contractData?.id || '',
        greeting: signingFormData.greeting,
        recipientDto: {
          email: email || '',
          familyName: lastName,
          givenName: firstName,
          language: signingFormData.language,
          notificationMethod: signingFormData.notificationMethod,
          order: 1,
          role: {
            action: 'sign',
            label: 'Signer',
            name: 'signer',
          },
          signingMethod: signingFormData.signingMethod,
          sms: signingFormData.sms,
          telephone: phone || '',
        },
      },
      {
        onSuccess: () => {
          if (id)
            updateContractMutation(
              {body: {...contractData, status: ContractStatus.SENT_FOR_SIGNING}, id},
              {
                onSuccess: () => {
                  setSuccessMessage(t(TranslationKey['Sent for signing']))
                },
              },
            )
        },
      },
    )
  }

  /**
   * A function handling Download button click
   */
  const onDownloadBtnClick = () => {
    if (pdfUrl) {
      const link = document.createElement('a')
      link.href = pdfUrl
      link.download = `${contractData?.customer.firstName} ${contractData?.customer.lastName} - ${t(TranslationKey.Contract)}`
      link.click()
    }
  }

  /**
   * A function handling Download button click
   */
  const onDownloadTransferBtnClick = () => {
    if (pdfTransferUrl) {
      const link = document.createElement('a')
      link.href = pdfTransferUrl
      link.download = `${dealerData?.name} - ${t(TranslationKey.Contract)}`
      link.click()
    }
  }

  const btnVisibilityModel = useMemo(
    () => ({
      viewPdf: true,
      /**
       * View Transfer PDF is enabled if Contract is not a quote
       */
      viewTransferPDf: contractStatus !== ContractStatus.QUOTE && contractStatus !== ContractStatus.CANCELED,
      /**
       * Edit button is enabled if Contract is not Sent for signing and not expired
       */
      completeQuoteOrEditInGeneral:
        contractData?.status !== ContractStatus.SENT_FOR_SIGNING &&
        !checkDateAfterToday(contractData?.expiry || '') &&
        contractData?.status !== ContractStatus.SIGNED,
      /**
       * Edit quote btn is visible if Contract is a quote and not expired
       */
      editQuote: contractData?.status === ContractStatus.QUOTE && !checkDateAfterToday(contractData?.expiry || ''),
      /**
       * Approve button is enabled for Active contracts and Super Administrator users
       */
      approve: contractData?.status === ContractStatus.ACTIVE && isSuperAdmin(),
      /**
       * Cancel button is enabled if Contract is not already Sent, Signed or Cancelled
       */
      cancel:
        contractData?.status !== ContractStatus.SENT_FOR_SIGNING &&
        contractData?.status !== ContractStatus.CANCELED &&
        contractData?.status !== ContractStatus.SIGNED,
    }),
    [contractData],
  )

  const btnConfig = {
    viewPdf: {
      label: t(TranslationKey['View PDF']),
      onClick: () => {
        setIsPdfModalOpen(true)
      },
      variant: 'outlined',
    },

    viewTransferPDf: {
      label: t(TranslationKey['View Transfer PDF']),
      onClick: () => {
        setIsPdfTransferModalOpen(true)
      },
      variant: 'outlined',
    },

    completeQuoteOrEditInGeneral: {
      label: t(TranslationKey[contractStatus === ContractStatus.QUOTE ? 'Edit quote' : 'Edit']),
      onClick: () => navigate(`${NavigationPathKey.CONTRACT_CREATION}/${id}`),
      variant: 'contained',
    },

    editQuote: {
      label: t(TranslationKey['Edit quote']),
      onClick: () => navigate(`${NavigationPathKey.CONTRACT_CREATION}/${id}?editQuote=true`),
      variant: 'contained',
    },

    approve: {
      label: t(TranslationKey['Approve']),
      onClick: () => setIsSignatureOptionsModalVisible(true),
      variant: 'contained',
    },

    cancel: {
      label: 'Cancel',
      onClick: () => void onCancelBtnClick(),
      variant: 'cancel',
    },
  } as const

  const filteredBtns = useMemo(
    () =>
      !contractData
        ? {}
        : Object.fromEntries(
            Object.entries(btnConfig).filter(([key]) => btnVisibilityModel[key as keyof typeof btnVisibilityModel]),
          ),
    [btnVisibilityModel],
  )

  return {
    onDownloadBtnClick,
    setIsSignatureOptionsModalVisible,
    onSendForSigningBtnClick,
    onDownloadTransferBtnClick,
    setIsPdfTransferModalOpen,
    isPdfTransferModalOpen,
    pdfTransferUrl,
    isPdfModalOpen,
    contractDetailFormFields,
    loadingPdf,
    setIsPdfModalOpen,
    signingFormFields,
    isSignatureOptionsModalVisible,
    pdfUrl,
    contractOverviewFormFields,
    filteredBtns,
    contractStatus,
    t,
  }
}
